import React from 'react';
import {Button} from '@betareader/design-system';
import {withTranslation} from 'react-i18next';
import moment from 'moment';
import {useHistory} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';

import {
  markNotificationsAsRead,
  fetchNotifications,
  markAllNotificationsAsRead
} from '../../modules/notification';
import {authUtil, uxAnalyticsUtil} from '../../utils';
import Avatar from '../../components/avatar/avatar';
import './notifications.css';

export const NotificationsList = ({onClose, t}) => {
  const notifications = useSelector(state => state.notification.notifications);
  const unreadCount = useSelector(state => state.notification.unreadCount);
  const dispatch = useDispatch();
  const history = useHistory();

  const loadMoreNotifications = async () => {
    uxAnalyticsUtil.trackEvent({category: 'Notifications', action: 'load-more'});
    const before = notifications?.length
      ? new Date(notifications[notifications.length - 1].createdAt).getTime()
      : undefined;
    const idToken = await authUtil.getFreshIdToken();
    dispatch(fetchNotifications(idToken, {before}));
  };

  const markAllAsRead = async () => {
    uxAnalyticsUtil.trackEvent({
      category: 'Notifications',
      action: 'mark-all-notifications-read'
    });
    const idToken = await authUtil.getFreshIdToken();
    dispatch(markAllNotificationsAsRead(idToken));
  };

  const goToObject = async notification => {
    uxAnalyticsUtil.trackEvent({
      category: 'Notifications',
      action: 'clicked-notification'
    });
    let url = notification.url;
    if (notification.urlMetadata?.role === 'READER') {
      url = notification.readerUrl;
    }
    if (!url) return;
    const currentHost = `${window.location.protocol}//${window.location.host}`;
    const idToken = await authUtil.getFreshIdToken();
    if (!notification.read) {
      dispatch(markNotificationsAsRead(idToken, [notification._id]));
    }
    if (url.startsWith(currentHost)) {
      history.push(url.slice(currentHost.length));
    } else {
      window.location = url;
    }
    onClose?.();
  };

  return (
    <section className='br-notifications' aria-label={t('Notifications')}>
      <div className='br-notifications__header'>
        <h2>{t('Notifications')}</h2>
        {unreadCount > 0 && (
          <Button variant='ghost' size='small' onClick={markAllAsRead}>
            {t('MarkAllAsRead')}
          </Button>
        )}
      </div>
      <ul className='br-notifications__list'>
        {(!notifications || notifications.length === 0) && (
          <li className='br-notifications__empty'>{t('NothingHere')}</li>
        )}
        {notifications?.map(notification => (
          <li key={notification._id}
            className={notification.read
              ? 'br-notifications__entry br-notifications__entry--read'
              : 'br-notifications__entry'}>
            <button type='button' className='br-notifications__entry-button'
              onClick={() => goToObject(notification)}
              disabled={!notification.url}>
              <Avatar diameter={25}
                name={notification.subject?.displayName} />
              <span className='br-notifications__entry-content'>
                <strong>{notification.subject?.displayName}</strong>
                <span>{' '}{t(`notification_verb_${notification.verb}`)}{' '}
                  {notification.object?.title ?? ''}</span>
                {notification.message && <span>: {notification.message}</span>}
                <time dateTime={notification.createdAt}>
                  {moment(notification.createdAt).fromNow()}
                </time>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {notifications?.length > 0 &&
        !notifications[notifications.length - 1].isLast && (
          <div className='br-notifications__more'>
            <Button variant='ghost' size='small' onClick={loadMoreNotifications}>
              {t('LoadMore')}
            </Button>
          </div>
        )}
    </section>
  );
};

export default withTranslation()(NotificationsList);
